import { useCallback } from 'react';
import {
  AssessmentsLoginFailureReason,
  AssessmentsPendoEventType,
  AssessmentsPendoTrackEventPayload,
} from './types';
import { useAssessmentsPendoEvents } from './useAssessmentsPendoEvents';

interface LoginSucceededDetails {
  assessmentsId: string;
  activityId: string;
}

/**
 * Pendo tracking for the assessments login: page viewed, succeeded, failed.
 * Tenant and survey event ids are already the Pendo account and visitor, so payloads carry outcomes only.
 */
export const useAssessmentsLoginTracking = () => {
  const { trackEvent } = useAssessmentsPendoEvents();

  const track = useCallback(
    (payload: AssessmentsPendoTrackEventPayload) => {
      const { eventType, ...eventData } = payload; // eventType is the event name, not payload data
      trackEvent(eventType, eventData);
    },
    [trackEvent],
  );

  const trackLoginPageViewed = useCallback(() => {
    track({
      eventType: AssessmentsPendoEventType.LOGIN_PAGE_VIEWED,
    });
  }, [track]);

  const trackLoginSucceeded = useCallback(
    ({ assessmentsId, activityId }: LoginSucceededDetails) => {
      track({
        eventType: AssessmentsPendoEventType.LOGIN_SUCCEEDED,
        assessmentsId: assessmentsId ?? '',
        activityId: activityId ?? '',
      });
    },
    [track],
  );

  const trackLoginFailed = useCallback(
    (reason: AssessmentsLoginFailureReason, status?: number) => {
      track({
        eventType: AssessmentsPendoEventType.LOGIN_FAILED,
        reason,
        ...(status && { status: `${status}` }),
      });
    },
    [track],
  );

  return { trackLoginPageViewed, trackLoginSucceeded, trackLoginFailed };
};
